<template>
  <div
    class="partner-scroll"
    ref="viewport"
    @mouseenter="pause"
    @mouseleave="resume"
  >
    <div class="ps-track" ref="track" :style="trackStyle">
      <!-- 第一份 -->
      <div class="ps-group" ref="group">
        <div class="ps-item" v-for="(item, i) in list" :key="`a-${i}`">
          <img
            :src="item.url"
            :alt="item.name || ''"
            @load="scheduleMeasure"
            @error="scheduleMeasure"
          />
        </div>
      </div>
      <!-- 第二份：仅当需要滚动时才复制，用于无缝衔接 -->
      <div class="ps-group" v-if="needScroll" aria-hidden="true">
        <div class="ps-item" v-for="(item, i) in list" :key="`b-${i}`">
          <img :src="item.url" alt="" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PartnerScroll",
  props: {
    list: { type: Array, default: () => [] },
    // 每帧位移像素，越大越快（0.6 ≈ 36px/秒）
    speed: { type: Number, default: 0.6 },
    pauseOnHover: { type: Boolean, default: true },
  },
  data() {
    return {
      offset: 0, // 轨道整体位移
      groupWidth: 0, // 一份列表宽度 + 一个间距
      viewportWidth: 0,
      gapValue: 0,
      needScroll: false,
      rafId: null,
      paused: false,
    };
  },
  computed: {
    trackStyle() {
      return { transform: `translate3d(${-this.offset}px, 0, 0)` };
    },
  },
  watch: {
    list() {
      this.$nextTick(this.scheduleMeasure);
    },
  },
  mounted() {
    this.$nextTick(this.measure);
    // 窗口尺寸变化：防抖后重新测量
    this._onResize = () => {
      clearTimeout(this._rt);
      this._rt = setTimeout(this.measure, 120);
    };
    window.addEventListener("resize", this._onResize);
    // 图片加载 / 布局变化导致宽度变化时自动重测
    if (typeof ResizeObserver !== "undefined") {
      this._ro = new ResizeObserver(this.scheduleMeasure);
      this.$nextTick(() => {
        if (this.$refs.group) this._ro.observe(this.$refs.group);
      });
    }
  },
  beforeDestroy() {
    this.stop();
    clearTimeout(this._mt);
    clearTimeout(this._rt);
    window.removeEventListener("resize", this._onResize);
    if (this._ro) this._ro.disconnect();
  },
  methods: {
    scheduleMeasure() {
      clearTimeout(this._mt);
      this._mt = setTimeout(this.measure, 50);
    },

    // 实测尺寸：gap 和宽度都从 DOM 读，CSS 随便改都不影响逻辑
    measure() {
      const vp = this.$refs.viewport;
      const group = this.$refs.group;
      if (!vp || !group) return;

      const gap = parseFloat(getComputedStyle(group).columnGap) || 0;
      const gw = group.getBoundingClientRect().width + gap;
      const vw = vp.clientWidth;

      // 数值没实质变化就不做任何更新，避免 ResizeObserver 抖动
      const changed =
        Math.abs(gw - this.groupWidth) > 0.5 ||
        Math.abs(vw - this.viewportWidth) > 0.5 ||
        this.gapValue !== gap;
      if (!changed) return;

      this.gapValue = gap;
      this.groupWidth = gw;
      this.viewportWidth = vw;

      // 内容没超出容器就不滚，省掉无意义的动画
      const should = gw - gap > vw + 1;
      if (should !== this.needScroll) {
        this.needScroll = should;
        this.offset = 0;
      }
      if (gw > 0 && this.offset >= gw) this.offset %= gw;

      if (!this.needScroll) this.stop();
      else if (!this.paused) this.start();
    },

    start() {
      if (this.rafId || !this.needScroll) return;
      this.paused = false;
      this.rafId = requestAnimationFrame(this.step);
    },

    step() {
      this.offset += this.speed;
      // 走满一份就无缝归零
      if (this.offset >= this.groupWidth) this.offset -= this.groupWidth;
      this.rafId = requestAnimationFrame(this.step);
    },

    stop() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    },

    pause() {
      if (!this.pauseOnHover) return;
      this.paused = true;
      this.stop();
    },

    resume() {
      if (!this.pauseOnHover) return;
      if (this.needScroll) this.start();
    },
  },
};
</script>

<style scoped>
.partner-scroll {
  overflow: hidden;
  height: 110px;
  /* 尺寸与间距统一在这里改，JS 会自动读取 */
  --ps-item-w: 160px;
  --ps-item-h: 100px;
  --ps-gap: 15px;
}
.ps-track {
  display: flex;
  width: max-content;
  gap: var(--ps-gap);
  will-change: transform;
}
.ps-group {
  display: flex;
  gap: var(--ps-gap);
  flex-shrink: 0;
}
.ps-item {
  width: var(--ps-item-w);
  height: var(--ps-item-h);
  flex-shrink: 0;
}
.ps-item img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

@media (max-width: 576px) {
  .partner-scroll {
    height: 90px;
    --ps-item-w: 130px;
    --ps-item-h: 80px;
    --ps-gap: 10px;
  }
}
</style>


// WEBPACK FOOTER //
// src/utils/PartnerScroll.vue